import moment from 'moment';
import { channelEnum } from '../constants/app';
import { checkHaiAuthorization } from '../services/app';
import {
  AppParamsProps,
  AuthRes,
  ChannelSourceType,
  ReportParams,
  Res,
  RetryResult,
  onQueryHaiIdParams,
} from './app.type';
import { getPublicConfig } from '../utils/config';
import urlUtils from '../utils/urlUtils';
import { logWorkSpaceSpeed } from '../routes/home/gaLog';

export function retry<T>(fn: () => Promise<T>, maxRetry = 5, interval = 200): RetryResult<T> {
  let retryCount = 0;
  let cancelFlag = false;

  const doRetry = (): Promise<T> => {
    return fn().catch((error) => {
      console.error(`Failed to execute function, retry ${retryCount}:`, error);
      if (++retryCount > maxRetry) {
        throw error;
      }
      return new Promise((resolve) => {
        const timerId = setTimeout(resolve, interval);
        if (cancelFlag) {
          clearTimeout(timerId);
        }
      }).then(doRetry);
    });
  };

  const cancel = () => {
    cancelFlag = true;
  };

  return { promise: doRetry, cancel };
}

export const getAppParams = (): AppParamsProps => {
  const searchParams = getSearchParams();
  const keys = ['channel', 'region', 'instanceId', 'msg', 'code', 'autoOpen', 'locale'];
  return keys.reduce((pre, key) => {
    return {
      ...pre,
      [key]: searchParams.get(key),
    };
  }, {}) as AppParamsProps;
};

export const getReportSearchParams = (): Record<string, string> => {
  const searchParams = getSearchParams();
  const reportKeyPrefix = 'report_';
  const reportParams = {};
  for (const [key, value] of searchParams) {
    if (key.startsWith(reportKeyPrefix) && key?.length > reportKeyPrefix?.length) {
      reportParams[key?.replace(reportKeyPrefix, '')] = value;
    }
  }
  return reportParams;
};

export const getSearchParams = () => {
  const searchParams = new URLSearchParams(location.search);
  return searchParams;
};

export const getChannelSource = () => {
  const { channel, instanceId } = getAppParams();
  const res: ChannelSourceType = {
    isHaiOriginCs: Boolean(channel === channelEnum.HAI && !instanceId),
    isHaiOriginHai: Boolean(channel === channelEnum.HAI && instanceId),
    isIntegration: channel === channelEnum.INTEGRATION,
    isNormal: channel !== channelEnum.INTEGRATION && channel !== channelEnum.HAI,
  };
  return res;
};

export const getReportParams = (): ReportParams => {
  const startTime = new Date().getTime();
  return { startTime };
};

export const onCheckHaiAuthorization = async (): Promise<AuthRes> => {
  const params = getReportParams();
  const res = await checkHaiAuthorization();
  return onResHandler(res, params);
};

export const onResHandler = async <T>(res: Res<T>, reportParams?: ReportParams) => {
  if (res?.code === 0) {
    return res?.data;
  } else {
    const { code, traceId, message: tipStr, path: errorApi, msg } = res || {};
    const { startTime = new Date().getTime(), open = true, onError } = reportParams || {};
    if (open) {
      const config = await getPublicConfig();
      const { channel } = getAppParams();
      const err = `status=fail, code=${code}, traceId=${traceId}, tipStr=${tipStr || msg}-${moment(startTime).format(
        'YY-MM-DD HH:mm:ss',
      )}, reportTime=${moment().format('YY-MM-DD HH:mm:ss')}, errorApi=${errorApi}, errorPageUrl=${
        window?.location?.href
      }, userId=${config?.userInfo?.data?.id}, outerHref=${urlUtils.outerHref()}, channel=${channel}`;

      logWorkSpaceSpeed('hai', '', err);
    }
    if (typeof onError === 'function') {
      return onError(res);
    }
    return Promise.reject(res);
  }
};

export const getRegionUrl = (params: onQueryHaiIdParams) => {
  return `https://${params.spaceKey}.${params.region}.cloudstudio.work`;
};

export const rmErrorParamsAndReload = () => {
  const url = new URL(location.href);
  url.searchParams.delete('msg');
  url.searchParams.delete('code');
  window.parent.location.href = url.href;
};

export const isErrorCase = () => {
  const { code, msg } = getAppParams();
  return code && msg;
};
